import { Button, Chip, Divider, Grid, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  deleteProcureVendor,
  getProcureVendorById,
  getProcureVendorsWithFilters,
  IssueType,
  PermissionsEnum,
  Property,
} from 'vendor-marketplace-shared-library';
import { useMutation, useQuery } from '@apollo/client';
import ProcureVendorDetailsModule from './components/ProcureVendorDetailsModule';
import ProcureVendorContactsModule from './components/ProcureVendorContactsModule';
import ProcureVendorWorkOrderModule from './components/ProcureVendorWorkOrderModule';
import React, { useState } from 'react';
import Loader from '../../components/common/Loader';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import { DELETE_PROCURE_VENDOR } from '../../error-messages';
import useAlert from '../../hooks/useAlert';
import useAuth from '../../hooks/useAuth';

export default function ProcureVendorDetailsPage() {
  const { id: vendorId } = useParams<{ id: string }>();
  const { validateUserPermissions } = useAuth();
  const alert = useAlert();
  const navigate = useNavigate();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { data, loading } = useQuery(getProcureVendorById, {
    variables: { id: vendorId },
    fetchPolicy: 'no-cache',
  });

  const [deleteVendor] = useMutation(deleteProcureVendor, {
    variables: {
      id: vendorId,
    },
    refetchQueries: [getProcureVendorsWithFilters],
    onCompleted: () => {
      navigate('/vendors');
    },
    onError: () => {
      alert.setAlert({ message: DELETE_PROCURE_VENDOR });
    },
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ProcureVendorDetailsModule vendor={data?.getProcureVendorById} />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {/*Properties*/}
      <Grid item container spacing={2}>
        {data?.getProcureVendorById.properties?.length > 0 && (
          <Grid item xs={12}>
            <Grid item container spacing={1} xs={12} sx={{ alignItems: 'center' }}>
              <Grid item>
                <Typography>Properties:</Typography>
              </Grid>
              {data?.getProcureVendorById.properties.map((property: Property) => (
                <Grid item key={property.id}>
                  <Chip label={property.name} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {/*Issue Types*/}
        {data?.getProcureVendorById?.issueTypes?.length > 0 && (
          <Grid item xs={12}>
            <Grid item container spacing={1} xs={12} sx={{ alignItems: 'center' }}>
              <Grid item>
                <Typography>Vendor trades:</Typography>
              </Grid>
              {data?.getProcureVendorById?.issueTypes.map((issueType: IssueType) => (
                <Grid item key={issueType.id}>
                  <Chip label={issueType.name} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
      {/*Vendor Contacts*/}
      <Grid item xs={12} md={6}>
        <ProcureVendorContactsModule vendor={data?.getProcureVendorById ?? {}} />
      </Grid>
      {/*Work Orders*/}
      <Grid item xs={12} md={6}>
        <ProcureVendorWorkOrderModule vendor={data?.getProcureVendorById ?? {}} />
      </Grid>
      {validateUserPermissions([PermissionsEnum.PROCURE_VENDOR_CONTACT_DELETE]) && (
        <Grid item>
          <Button onClick={() => setOpenDeleteModal(true)} color='error'>
            Delete this vendor
          </Button>
        </Grid>
      )}
      <DeleteConfirmationModal
        entity={'vendor'}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        onDelete={deleteVendor}
      />
    </Grid>
  );
}
